import React, { useState, useRef, useEffect } from "react";
import "./verify-otp.scss";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

const VerifyOtp = () => {
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [errors, setErrors] = useState([false, false, false, false, false, false]);
    const [countdown, setCountdown] = useState(30); // 30 seconds timer
    const [showCaptcha, setShowCaptcha] = useState(false);
    const [captchaValue, setCaptchaValue] = useState("");
    const [userCaptcha, setUserCaptcha] = useState("");

    const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
    const navigate = useNavigate();
    const location = useLocation();

    // Get method and contact from navigation state
    const { method, contact } = location.state || { method: 'email', contact: 'your registered email' };

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (countdown > 0) {
            timer = setInterval(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [countdown]);

    // Generate Captcha Code
    const generateCaptcha = () => {
        const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // No I, O, 1, 0 to avoid confusion
        let result = "";
        for (let i = 0; i < 6; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setCaptchaValue(result);
    };

    const handleChange = (value: string, index: number) => {
        if (isNaN(Number(value))) return;

        const newOtp = [...otp];
        newOtp[index] = value.substring(value.length - 1);
        setOtp(newOtp);

        // Clear error for this box
        const newErrors = [...errors];
        newErrors[index] = false;
        setErrors(newErrors);

        // Move to next box
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleVerify = () => {
        // Step 1: Verify OTP
        if (!showCaptcha) {
            const newErrors = otp.map((val) => val === "");
            setErrors(newErrors);

            if (newErrors.some((err) => err)) {
                toast.error("Please enter the complete OTP");
                return;
            }

            const otpValue = otp.join("");
            // Simulate verification (Accept 123456)
            if (otpValue !== "123456") {
                setErrors(new Array(6).fill(true));
                toast.error("Invalid OTP. Please try again.");
                return;
            }

            // OTP Valid -> Show Captcha
            toast.success("OTP Verified! Please complete security check.");
            generateCaptcha();
            setShowCaptcha(true);
        }
        // Step 2: Verify Captcha
        else {
            if (userCaptcha.toUpperCase() !== captchaValue) {
                toast.error("Invalid Captcha. Please try again.");
                generateCaptcha(); // Refresh on error
                setUserCaptcha("");
                return;
            }
            toast.success("Verification Successful!");
            navigate("/dashboard");
        }
    };

    const handleResend = (e: React.MouseEvent) => {
        e.preventDefault();
        if (countdown === 0) {
            setCountdown(30);
            toast.info("OTP has been resent!");
        }
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins < 10 ? "0" + mins : mins}:${secs < 10 ? "0" + secs : secs}`;
    };

    return (
        <div className="container-fluid h-p100 auth-container">
            <div className="row align-items-center justify-content-md-center h-p100">
                <div className="col-12">
                    <div className="row justify-content-center g-0">
                        <div className=""> {/* Width preserved as requested */}
                            <div className="rounded10 shadow-lg translucent-card p-40">
                                <div className="content-top-agile p-20 pb-0 text-center">
                                    <h2 >
                                        {showCaptcha ? "Security Check" : "Verify Your Account"}
                                    </h2>

                                    <p className="mb-0 text-dark fw-medium opacity-75">
                                        {showCaptcha ? "Please verify you are human." : "Secure your account with the code sent to you."}
                                    </p>
                                    {!showCaptcha && (
                                        <p className="text-dark mt-2 fs-16 opacity-75">
                                            Enter the 6-digit code sent to your {method === 'email' ? 'email' : 'mobile'}: <br />
                                            <span className="fw-bold text-dark opacity-100">{contact}</span>
                                        </p>
                                    )}
                                </div>

                                <div className="form-input-container mt-30">
                                    {!showCaptcha ? (
                                        <>
                                            <div className="otp-container d-flex justify-content-between mb-30">
                                                {otp.map((digit, index) => (
                                                    <input
                                                        key={index}
                                                        type="text"
                                                        maxLength={1}
                                                        value={digit}
                                                        ref={(el) => { inputRefs.current[index] = el; }} // Fixed ref callback
                                                        onChange={(e) => handleChange(e.target.value, index)}
                                                        onKeyDown={(e) => handleKeyDown(e, index)}
                                                        className={`form-control otp-input ${errors[index] ? "error" : ""} ${digit ? "filled" : ""}`}
                                                    />
                                                ))}
                                            </div>

                                           

                                            <div className="text-center">
                                                <button
                                                    type="submit"
                                                    className="btn btn-verify px-40"
                                                    onClick={handleVerify}
                                                >
                                                    VERIFY CODE
                                                </button>
                                            </div>

                                            <div className="text-center mt-30">
                                                <p className="mb-1 text-muted">Didn't receive the code?</p>
                                                {countdown > 0 ? (
                                                    <span className="text-danger fw-bold fs-16">
                                                        Resend in {formatTime(countdown)}
                                                    </span>
                                                ) : (
                                                    <a href="#" onClick={handleResend} className="text-primary fw-bold fs-16">
                                                        Resend Now
                                                    </a>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="captcha-step fade-in">
                                            <div className="d-flex align-items-center justify-content-center mb-4">
                                                <div className="captcha-display p-3 bg-light rounded border d-flex align-items-center justify-content-between" style={{ minWidth: '200px' }}>
                                                    <span className="fs-24 fw-bold text-primary letter-spacing-5 font-monospace unselectable">
                                                        {captchaValue.split('').map((char, i) => (
                                                            <span key={i} style={{ transform: `rotate(${Math.random() * 20 - 10}deg)`, display: 'inline-block' }}>{char}</span>
                                                        ))}
                                                    </span>
                                                    <button className="btn btn-link text-muted p-0 ms-3" onClick={generateCaptcha}>
                                                        <i className="fa fa-refresh fs-20"></i>
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="form-group mb-30">
                                                <input
                                                    type="text"
                                                    className="form-control text-center fs-20 fw-bold letter-spacing-2"
                                                    placeholder="Enter Captcha Code"
                                                    value={userCaptcha}
                                                    onChange={(e) => setUserCaptcha(e.target.value)}
                                                    maxLength={6}
                                                    style={{ height: '50px', borderRadius: '12px' }}
                                                    autoFocus
                                                />
                                            </div>

                                            <div className="text-center">
                                                <button className="btn btn-verify fw-bold shadow-sm" onClick={handleVerify}>
                                                    COMPLETE VERIFICATION
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VerifyOtp;
